import { RadioInput, TextInput, Tags, ResourceInput, Jsx, SelectInput, TextAreaInput } from "../utils/Input.js"
import i18n from "i18next"
import { TagsColumn, TextColumn } from "../utils/Columns.js"
import { PermissionLink } from "../../components/PermissionLink.jsx"
import {
  useAddCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  selectAllCustomers,
  useGetCustomersQuery,
} from "../../redux/customersSlice.js"
import { store } from "../../store.js"
import { selectRegieRegieContacts } from "../../redux/Contacts/regieContactSlice.js"
import { selectAllRegies } from "../../redux/Contacts/regieSlice.js"
import { EmployeeResource } from "../EmployeeResource.js"
import { RegieResource } from "../Contacts/RegieResource.js"
import { RegieContactResource } from "../Contacts/RegieContactResource.js"
import { SharepointUrl } from "../../components/SharepointValuePair.jsx"
import { Anchor, Box } from "grommet"
import { CUSTOMER_TAGS } from "../../utils/constants.js"
import { writeFileXLSX, utils } from "xlsx"
import { selectAllEmployeesWithUser } from "../../redux/employeesSlice.js"

export class CustomerRessource {
  static name = "customer"

  static resourcePath = "customers"

  static permissionPrefix = "customer"

  static indexTitle = "Clients"

  static oneTitle = "un client"

  static fuseConfig = [
    {
      name: "name",
      weight: 3,
    },
    {
      name: "company",
      weight: 3,
    },
    {
      name: "zipcity",
      weight: 4,
    },
    {
      name: "humanId",
      weight: 3,
    },
  ]

  static methods = {
    load: useGetCustomersQuery,
    add: useAddCustomerMutation,
    update: useUpdateCustomerMutation,
    delete: useDeleteCustomerMutation,
  }

  static loadDependencies() {
    return [
      EmployeeResource.methods.load(),
      RegieResource.methods.load(),
      RegieContactResource.methods.load()
    ]
  }

  static all() {
    return selectAllCustomers(store.getState())
  }

  static indexButtons = (_data, user) => {
    const buttons = []

    if (user.is_admin) {
      buttons.push({
        label: "Export XLS",
        onClick: ({ data }) => {
          const worksheet = utils.json_to_sheet(data.map(customer => ({
            "Numéro de client": customer.humanId,
            Nom: customer.name,
            Entreprise: customer.company,
            Rue: customer.getStreetAddress(),
            NPA: customer.zip_code,
            Localité: customer.city,
            Téléphone: customer.phone,
            Mobile: customer.mobile,
            Email: customer.email,
            Type: customer.type ? i18n.t(`customer.type.${customer.type}`) : null
          })));
          const workbook = utils.book_new();
          utils.book_append_sheet(workbook, worksheet, "Clients");
          const date = new Date().toISOString().split('T')[0];
          writeFileXLSX(workbook, `export-clients-${date}.xlsx`)
        },
      })
    }

    return buttons
  }

  static form(data = {}, user = {}) {
    const regies = selectAllRegies(store.getState()).map(c => ({
      value: c.id,
      label: c.getTitle(),
    }))

    const contacts = selectRegieRegieContacts(
      store.getState(),
      data.regie_id
    ).map(c => ({
      value: c.id,
      label: c.getTitle(),
    }))

    const employees = selectAllEmployeesWithUser(store.getState()).map(e => ({
      value: e.user_id,
      label: e.getTitle(),
    }))

    return {
      defaultValue: {
        type: "private",
      },
      fields: [
        new SelectInput("user_id")
          .label("Account manager")
          .options(employees)
          .get(),

        new RadioInput("type")
          .label("Type")
          .options([
            { label: "Privé", value: "private" },
            { label: "Entreprise", value: "company" },
            { label: "PPE", value: "ppe" },
          ])
          .addInputProps({ direction: "row" })
          .defaultValue("private")
          .display(() => i18n.t(`customer.type.${data.type}`))
          .get(),

        new Jsx("sharepoint")
          .label("Fichiers")
          .display(() => <SharepointUrl url={data.getDriveFolderUrl()} />)
          .get(),

        new Jsx("humanId")
          .label("Numéro de prospect")
          .display(() => data.humanId)
          .get(),

        new Jsx("address")
          .label("Coordonnées")
          .display(() => (
            <Box
              style={{ color: "#777776", fontSize: "18px", lineHeight: "24px" }}
            >
              {data.getAddressLines().map((line, i) => (
                <div key={i}>{line}</div>
              ))}
              {data.email && (
                <Anchor href={`mailto:${data.email}`}>{data.email}</Anchor>
              )}
              {data.phone && (
                <Anchor href={`tel:${data.phone.replaceAll(" ", "")}`}>
                  {data.phone}
                </Anchor>
              )}
              {data.mobile && (
                <Anchor href={`tel:${data.mobile.replaceAll(" ", "")}`}>
                  {data.mobile}
                </Anchor>
              )}
            </Box>
          ))
          .get(),

        new Tags("tags")
          .options(CUSTOMER_TAGS)
          .label("Étiquettes")
          .defaultValue(["HabitatJardin2025"])
          .get(),

        new RadioInput("title")
          .label("Titre")
          .hiddenOnDetail()
          .addInputProps({ direction: "row" })
          .options([
            { label: "Mme", value: "mrs" },
            { label: "Mr", value: "mr" },
          ])
          .get(),

        new TextInput("company")
          .label("Entreprise")
          .hidden(user.hasRole("foire"))
          .hiddenOnDetail()
          .required(data.type === "company")
          .get(),

        new TextInput("name")
          .label("Nom & prénom")
          .hiddenOnDetail()
          .required()
          .get(),

        new TextInput("street")
          .hiddenOnDetail()
          .label("Rue")
          .hidden(user.hasRole("foire"))
          .get(),

        new TextInput("street_number")
          .hiddenOnDetail()
          .label("Numéro")
          .hidden(user.hasRole("foire"))
          .get(),

        new TextInput("zip_code").hiddenOnDetail().label("NPA").get(),

        new TextInput("city").hiddenOnDetail().label("Localité").get(),

        new TextInput("parcel_number")
          .label("Numéro de parcelle")
          .hidden(user.hasRole("foire"))
          .get(),

        new TextInput("phone")
          .hiddenOnDetail()
          .label("Téléphone")
          .hidden(user.hasRole("foire"))
          .get(),

        new TextInput("mobile").hiddenOnDetail().label("Tél. mobile").get(),

        new TextInput("email")
          .hiddenOnDetail()
          .label("Email")
          .hidden(user.hasRole("foire"))
          .get(),

        new ResourceInput("regie_id")
          .label("Régie")
          .options(regies)
          .resourcePath("regies")
          .permission("regie_view")
          .searchable()
          .hiddenOnDetail(!data?.regie_id)
          .get(),

        new ResourceInput("regie_contact_id")
          .label("Contact dans la régie")
          .options(contacts)
          .resourcePath("regie_contacts")
          .permission("regie_contact_view")
          .searchable()
          .hidden(!data?.regie_id)
          .hiddenOnDetail(!data?.regie_contact_id)
          .get(),

        new TextAreaInput("inquiry")
          .label("Demande initiale du client")
          .get()
      ],
    }
  }

  static table() {
    return {
      filters: [
        new Tags("tags").options(CUSTOMER_TAGS).label("Étiquettes").get(),
      ],
      columns: [
        new TextColumn("name")
          .label("Nom")
          .render(item => (
            <PermissionLink
              to={`/customers/${item.id}`}
              permission={`${this.permissionPrefix}_view`}
            >
              {item.getTitle()}
              <br />
              <small style={{ color: "#777" }}>{item.humanId}</small>
            </PermissionLink>
          ))
          .get(),

        new TextColumn("zipcity").label("Localité").get(),

        new TextColumn("phone").label("Téléphone").get(),

        new TextColumn("mobile").label("Mobile").get(),

        new TextColumn("email").label("Email").get(),

        new TagsColumn("tags").label("Étiquettes").get(),
      ],
    }
  }
}
